export interface CookieConsentState {
  necessary: true;
  functional: boolean;
  statistical: boolean;
  marketing: boolean;
}

const COOKIE_NAME = 'gb_consent';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

export const useCookieConsent = () => {
  const consentCookie = useCookie<CookieConsentState | null>(COOKIE_NAME, {
    maxAge: COOKIE_MAX_AGE,
    sameSite: 'strict',
    default: () => null
  });

  const hasConsented = computed(() => consentCookie.value !== null);
  const hasFunctional = computed(
    () => consentCookie.value?.functional ?? false
  );
  const hasStatistical = computed(
    () => consentCookie.value?.statistical ?? false
  );
  const hasMarketing = computed(() => consentCookie.value?.marketing ?? false);

  function syncPostHogConsent(statistical: boolean) {
    if (import.meta.client) {
      const { $posthog } = useNuxtApp();
      const ph = $posthog?.();
      if (!ph) return;
      if (statistical) {
        ph.opt_in_capturing();
      } else {
        ph.opt_out_capturing();
      }
    }
  }

  function acceptAll() {
    consentCookie.value = {
      necessary: true,
      functional: true,
      statistical: true,
      marketing: true
    };
    syncPostHogConsent(true);
  }

  function acceptNecessaryOnly() {
    consentCookie.value = {
      necessary: true,
      functional: false,
      statistical: false,
      marketing: false
    };
    syncPostHogConsent(false);
  }

  function savePreferences(prefs: Omit<CookieConsentState, 'necessary'>) {
    consentCookie.value = {
      necessary: true,
      ...prefs
    };
    syncPostHogConsent(prefs.statistical);
  }

  return {
    hasConsented,
    hasFunctional,
    hasStatistical,
    hasMarketing,
    acceptAll,
    acceptNecessaryOnly,
    savePreferences
  };
};
